import { useMemo } from 'react';
import api from '../../services/api';
import { cliApiBaseFlag, PRODUCTION_API } from '../../utils/cliApiBase';

/**
 * The paste a coding assistant is handed — built in ONE place.
 *
 * New app (CreateFlow) and first-run onboarding (Welcome) both hand somebody
 * this text. Two copies of it would drift the first time either was edited, and
 * the two pages would then set the same machine up two different ways.
 */

export const TOKEN_PLACEHOLDER = '<YOUR_TOKEN>';

/* THE READING LIST, AND IT IS THE POINT OF THE PASTE.
   The goal is an assistant that can do the WHOLE build, which means the only
   things left for a person are the two nobody else can know: where it deploys,
   and what it should feel like. Everything else the platform already decided and
   wrote down — so the paste names the pages rather than summarising them, because
   a summary in a paste is a second copy of the docs that goes stale.

   ORDERED AS A BUILD, not alphabetically. Concepts before permissions before
   schemas: an assistant that reads schemas first invents one, and a schema you
   invent instead of reusing is invisible to every other app, permanently. */
const READING = [
  ['quickstart', 'the shortest path end to end'],
  ['concepts', 'nodes, edges and identity; read this one first'],
  ['permissions', 'realms, exposure profiles, approval'],
  ['schemas', 'REUSE a schema; one you invent is invisible to every other app'],
  ['apps-and-plugins', 'an app or a plugin; a plugin\'s tools, what each reads and writes, and how an app uses one'],
  ['crux', 'what an app will be graded on; a plugin has none'],
  ['shell', 'the frontend framework, so an app looks like it belongs'],
  ['personas', 'only if it needs an agent'],
];

/* THE DOCS, AS AN ABSOLUTE URL, BUILT FROM WHERE YOU ALREADY ARE.
   A paste goes into a terminal and then into an assistant, so a relative path is
   useless there — it needs a host. And the host cannot be written down: this
   portal answers on developer.ofself.ai AND developer.ofself.com (see
   apiConfig), so a hardcoded one would send half the developers to the other
   domain, and a local dev to production. `origin` is the only value that is
   right for whoever is reading the page. */
export const DOCS_PATH = '/documentation';
export const docsURL = () => `${window.location.origin}${DOCS_PATH}`;

/** The reading list as paste-able lines. */
export const readingBlock = () => READING
  .map(([slug, why]) => `  ${docsURL()}/${slug} — ${why}`)
  .join('\n');

/* THE TWO THINGS A DEVELOPER STILL HAS TO SUPPLY, named so the assistant asks
   for them instead of inventing them. Deployment is not in the docs because it
   is not ours — it is your infrastructure. Shell opinions are not in the docs
   because taste is not a fact. Everything ELSE is written down, and saying which
   two are missing is what turns "read the docs" into "you have everything but
   these". */
export const OWNER_SUPPLIES = {
  app: `Two things are not in the docs and only I can give you — ask me:\n`
    + `  1. Deployment — where this runs and how it ships.\n`
    + `  2. Shell opinions — how it should look and feel in the ofSelf shell.\n`
    + `Do not guess at either. Everything else is in the pages above, or comes from\n`
    + `\`paradigm readiness\` and \`paradigm crux check\`.`,
  // A plugin has no screen of its own and no CRUX: only where it runs is mine.
  plugin: `One thing is not in the docs and only I can give you — ask me:\n`
    + `  Deployment — where its endpoint runs. ofSelf calls it from its own servers,\n`
    + `  so localhost is not reachable.\n`
    + `Do not guess at it. Everything else is in the pages above, or comes from\n`
    + `\`paradigm plugin check\` and \`paradigm plugin health\`.`,
};

/* ── the CLI's own idea of where it is talking to ─────────────────────────
 * A developer on a non-default backend needs --api-base on the very first
 * command or nothing after it works, and they have no way to know that. */
export function useRemoteFlag() {
  return useMemo(() => cliApiBaseFlag(api.getBaseUrl?.()), []);
}

/**
 * The paste for starting something new: install, log in, scaffold, read.
 * `token` null leaves the placeholder in, so the text is never half-built.
 */
export function newBuildHandoff({ kind, token, groupId, groupName, remote = '' }) {
  const t = token || TOKEN_PLACEHOLDER;
  const setup =
    `pipx install --force paradigm-cli --pip-args="--extra-index-url `
    + `https://paradigm:${t}@${new URL(PRODUCTION_API).host}/api/pypi/simple/" && `
    + `paradigm login --token ${t}${groupId ? ` --group-id ${groupId}` : ''}${remote}`;

  /* OWNERSHIP IS CARRIED, not assumed. `paradigm init` asks whether to register
     under your active group context, and answering that question wrong gives
     somebody an app under the wrong identity with nothing downstream flagging
     it. The flag is in the prompt so the agent never has to guess. */
  const ownerFlag = groupId ? ` --group-id ${groupId}` : ' --personal';
  const ownerLine = groupId
    ? `This ${kind} belongs to the group ${groupName || groupId}.`
    : `This ${kind} is mine personally, not a group's.`;

  return `I want to build an ofSelf ${kind} that [describe it in a sentence].\n\n`
    + `${ownerLine}\n\n`
    + `Set this machine up, then scaffold the project and design it with me:\n\n`
    + `  ${setup}\n`
    + `  paradigm ${kind === 'plugin' ? 'init --type plugin' : 'init'} --name <short-name>${ownerFlag}${remote}\n\n`
    + `\`init\` writes a .claude/skills/ directory — follow the paradigm-${kind} skill it puts there.\n`
    + (kind === 'plugin'
      ? `A plugin has no CRUX: its contract is plugin.yaml. It offers tools, and each tool\n`
        + `declares what it reads and writes (a plugin with no tools is one tool). Survey\n`
        + `the schema registry before deciding what each reads and writes, then\n`
        + `\`paradigm plugin check\` and \`paradigm plugin push\` to register it.\n\n`
      : `Start at the CRUX: survey the schema registry before asking me anything.\n\n`)
    // THE SKILLS ARE NOT THE DOCS. `init` writes a skill directory, which is the
    // recipe for scaffolding THIS project — the docs are what the platform does,
    // and an assistant with only the skill invents endpoints once it steps
    // outside the recipe. Both, named separately, so neither reads as the other.
    + `Read these first — they are the platform, not a tutorial:\n`
    + `${readingBlock()}\n\n`
    + OWNER_SUPPLIES[kind === 'plugin' ? 'plugin' : 'app'];
}
